// AssetLengthControl — Phase 5 Task 2.
//
// User-editable timeline length for audio-only TTS/STS assets that have no
// admin `targetDuration` and no video loaded. Writes into
// `useSessionStore.customDurations[assetIdx]`; Timeline re-reads this via
// `getAssetTimelineDuration` so the ruler updates automatically.
//
// On shrink we also run `sweepClampOffsets` so any favorited chips that now
// fall past `totalDuration` get pulled back + sweep-resolved in one commit
// (legacy `setTimelineLength`, index.html line 6163).
//
// Visibility rules (mirror legacy `needsLengthInput` at line 6114):
//   - asset.assetType is 'tts' or 'sts' (or custom in 'tts'/'sts' mode)
//   - no video loaded for this asset (blob or admin videoPath absent)
//   - admin did NOT set asset.targetDuration
//
// Controlled mode (FB2#12 — 2026-04-28). When the caller passes `value` +
// `onChange`, the component skips its own store read/write and the visibility
// gate (`forceVisible`), letting it be reused in places like FreeAssetHeader
// where the parent already owns the asset-context decision. The input itself
// is now a `type="text"` field with local string state so the user can freely
// type "20" → "20.5" → ".5" without the number-input arrow-step quantizing
// behind their cursor (FB2#12).
//
// Commit timing (GitHub #2, 2026-09-15): the value is committed on blur and
// Enter only. Typing just updates the local draft — an earlier per-keystroke
// commit meant "select all, type 18" pushed the timeline to 1 s at the `1`
// and ran the sweep-clamp against that, shoving every chip left before the
// `8` ever arrived. Parse/format rules live in `src/lib/assetLength.ts`.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { DEFAULT_ASSET_DURATION, MAX_ASSET_DURATION } from '@shared/constants';
import { formatAssetLength, parseAssetLength } from '@/lib/assetLength';
import { useSessionStore } from '@/stores/session';
import { useVideoStore } from '@/stores/video';
import { sweepClampOffsets } from '@/lib/timelineSweep';
import styles from './VideoPlayer.module.css';

interface AssetLengthControlProps {
  /** Controlled value in seconds. When provided alongside `onChange` the
   *  component bypasses its store read/write and acts as a controlled input. */
  value?: number;
  /** Controlled-mode change handler. Receives parsed seconds (0 = clear /
   *  fall through to default). Caller is responsible for sweep-clamping
   *  existing chips on shrink. */
  onChange?: (seconds: number) => void;
  /** Skip the visibility gate (asset must be TTS/STS without video/admin
   *  duration). Use when the parent already established the right context
   *  (e.g. FreeAssetHeader for custom assets). */
  forceVisible?: boolean;
}

export function AssetLengthControl({
  value: controlledValue,
  onChange: controlledOnChange,
  forceVisible,
}: AssetLengthControlProps = {}) {
  const isControlled = controlledValue !== undefined && controlledOnChange !== undefined;

  const assetIdx = useSessionStore((s) => s.currentAssetIdx);
  const configAssets = useSessionStore((s) => s.config?.assets);
  const customAssets = useSessionStore((s) => s.customAssets);
  const customDurations = useSessionStore((s) => s.customDurations);
  const setCustomDuration = useSessionStore((s) => s.setCustomDuration);
  const updateActiveBlocks = useSessionStore((s) => s.updateActiveBlocks);
  const videoBlobs = useVideoStore((s) => s.blobs);

  // Resolve the active asset (mirrors VideoPlayer's local helper to avoid
  // subscribing to selectAllAssets, which rebuilds its array each call).
  const asset = useMemo(() => {
    const merged = [
      ...(configAssets ?? []),
      ...customAssets.map((ca) => ({
        ...ca,
        assetType: 'free' as const,
        _isCustom: true as const,
      })),
    ];
    return merged[assetIdx];
  }, [configAssets, customAssets, assetIdx]);

  // For custom assets, the "effective" assetType is whatever freeMode they
  // last picked. That picker lives in the Generation view — for now we treat
  // custom assets as TTS-ish if no mode is set yet; the legacy code did the
  // same via `freeMode || 'tts'`.
  const effectiveAssetType: 'tts' | 'sts' | 'free' | 'neither' = useMemo(() => {
    if (!asset) return 'neither';
    if (asset.assetType === 'tts' || asset.assetType === 'sts') return asset.assetType;
    return 'neither';
  }, [asset]);

  const hasBlob = !!videoBlobs[assetIdx];
  const hasAdminPath = !!asset?.videoPath;
  // Predefined assets honour admin `allowVideo`; custom assets always have
  // the Video Reference panel available, so any loaded blob counts as a
  // resolved video source for length-control visibility.
  const videoEnabled = asset?._isCustom ? true : !!asset?.allowVideo;
  // Only show when no video source can resolve (legacy needsLengthInput).
  const hasAnyVideoSource = videoEnabled && (hasBlob || hasAdminPath);
  const visible = forceVisible || (
    (effectiveAssetType === 'tts' || effectiveAssetType === 'sts') &&
    !hasAnyVideoSource &&
    !asset?.targetDuration
  );

  // Source-of-truth numeric value. Controlled mode: parent owns it. Store
  // mode: read from `customDurations`, fall back to default so the input
  // never renders empty when the user hasn't typed anything yet.
  const numericValue = isControlled
    ? (controlledValue ?? 0)
    : (customDurations[assetIdx] ?? DEFAULT_ASSET_DURATION);

  // Local string state — what the input actually displays. Lets the user
  // freely type partial values like ".5" or "" without fighting the
  // numeric source-of-truth (FB2#12). We sync from `numericValue` only
  // when it changes EXTERNALLY (asset switch, sweep-clamp, etc.) — typing
  // never bounces back through this effect because we update the local
  // state synchronously in `handleInput`.
  const [draft, setDraft] = useState(() => formatAssetLength(numericValue));
  const [hasFocus, setHasFocus] = useState(false);
  useEffect(() => {
    // Don't stomp the user's in-flight edits.
    if (hasFocus) return;
    setDraft(formatAssetLength(numericValue));
  }, [numericValue, hasFocus]);

  const commit = useCallback(
    (secs: number) => {
      if (isControlled) {
        controlledOnChange!(secs);
        return;
      }
      setCustomDuration(assetIdx, secs);
      // Clamp + sweep existing chips so dropping below current chip ends
      // doesn't leave them "floating" past the ruler. Pure no-op when all
      // chips already fit. Per-track sweep (#173): cross-voice chips can
      // keep overlapping; only same-track collisions get resolved.
      const voiceIds = asset?.voiceIds ?? [];
      updateActiveBlocks((prev) => sweepClampOffsets(prev, secs, voiceIds));
    },
    [isControlled, controlledOnChange, assetIdx, setCustomDuration, updateActiveBlocks, asset?.voiceIds],
  );

  // Typing only updates the draft. Nothing reaches the store until the
  // field loses focus or the user hits Enter — see header comment.
  const handleInput = useCallback((raw: string) => setDraft(raw), []);

  // Blur / Enter: parse the draft once, commit it (store write + sweep or
  // controlled onChange) and re-format the field to the canonical "x.y" /
  // empty form so a stray trailing dot or extra zeros don't linger. We
  // format from the parsed value rather than `numericValue` because the
  // store round-trip hasn't re-rendered yet at this point.
  const commitDraft = useCallback(() => {
    const secs = parseAssetLength(draft);
    commit(secs);
    setDraft(formatAssetLength(secs));
  }, [draft, commit]);

  const handleBlur = useCallback(() => {
    setHasFocus(false);
    commitDraft();
  }, [commitDraft]);

  if (!visible) return null;

  return (
    <div className={styles.assetLength}>
      <span className={styles.assetLengthLabel}>Asset Length:</span>
      <input
        type="text"
        inputMode="decimal"
        value={draft}
        onChange={(e) => handleInput(e.target.value)}
        onFocus={() => setHasFocus(true)}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        className={styles.assetLengthInput}
        aria-label="Asset length in seconds"
      />
      <span>s (max {MAX_ASSET_DURATION})</span>
    </div>
  );
}
